import type React from "react";

export type IncompatibleFile = {file: File; reason: string; index: number};

export const VARIANT_CLASSES = {
   common:
      "w-full bg-background text-1000 resize-none border-b-0 focus:outline-none focus:ring-0 border-b-0 -mb-2 border-0 placeholder:text-500 dark:placeholder:text-600",
   large: "px-5 pt-5 pb-4 text-base",
   compact: "p-4",
};

export const CONTROLS_CLASSES = {
   common: "bg-background",
   large: "px-4 py-2",
   compact: "px-4 py-2",
};

export const UPLOAD_BUTTON_CLASSES =
   "h-8 w-8 p-0 rounded-full bg-200 text-800 hover:bg-300 disabled:bg-100 disabled:opacity-50 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-300 transition-colors duration-200";

export function getScrollMaskStyle(canScrollLeft: boolean, canScrollRight: boolean): React.CSSProperties {
   const left = canScrollLeft ? "transparent 0px, black 20px" : "black 0px";
   const right = canScrollRight ? "calc(100% - 20px), transparent 100%" : "100%";
   const gradient = `linear-gradient(to right, ${left}, black ${right})`;
   return {maskImage: gradient, WebkitMaskImage: gradient} as React.CSSProperties;
}

export function adjustTextareaHeight(
   textarea: HTMLTextAreaElement,
   variant: "large" | "compact",
   maxRows: number
) {
   // The expanded composer locks its height. Measuring then would store that
   // stretched height inline, and it would stick after the lock is removed.
   if (textarea.dataset?.composerExpanded === "true") return;

   textarea.style.height = "auto";
   const lineHeight = 24;
   const maxHeight = lineHeight * maxRows;

   if (textarea.scrollHeight <= maxHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = "hidden";
   } else {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = "auto";
   }
}

/**
 * Visual rows of text, including soft wraps. A textarea that has not been laid
 * out (or jsdom) reports no width, so explicit newlines are the fallback.
 * The live field may be height-locked while expanded, so a hidden clone is
 * measured instead of the field itself.
 */
export function countTextareaRows(textarea: HTMLTextAreaElement): number {
   const newlineRows = Math.max(1, textarea.value.split("\n").length);
   const width = textarea.clientWidth;
   if (width <= 0 || typeof document === "undefined") return newlineRows;

   const probe = textarea.cloneNode(false) as HTMLTextAreaElement;
   probe.removeAttribute("id");
   probe.removeAttribute("data-testid");
   probe.removeAttribute("aria-controls");
   probe.removeAttribute("aria-activedescendant");
   probe.removeAttribute("aria-expanded");
   probe.setAttribute("aria-hidden", "true");
   probe.tabIndex = -1;
   probe.rows = 1;
   probe.style.setProperty("position", "absolute");
   probe.style.setProperty("top", "0");
   probe.style.setProperty("left", "0");
   probe.style.setProperty("visibility", "hidden");
   probe.style.setProperty("pointer-events", "none");
   probe.style.setProperty("height", "0px", "important");
   probe.style.setProperty("min-height", "0", "important");
   probe.style.setProperty("max-height", "none", "important");
   probe.style.setProperty("width", `${width}px`, "important");
   const computed = getComputedStyle(textarea);
   probe.style.font = computed.font;
   probe.style.letterSpacing = computed.letterSpacing;

   const measure = (value: string) => {
      probe.value = value;
      return probe.scrollHeight;
   };

   document.body.appendChild(probe);
   try {
      const oneLine = measure("X");
      const lineHeight = measure("X\nX") - oneLine;
      if (lineHeight <= 0) return newlineRows;
      const content = measure(textarea.value);
      const wrappedRows = 1 + Math.round((content - oneLine) / lineHeight);
      return Math.max(1, newlineRows, wrappedRows);
   } finally {
      probe.remove();
   }
}
